import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'June 1, 2025';
const VERSION = '1.0';
const CONTACT_EMAIL = 'info@themoneymap.online';
const CONTACT_ADDRESS = 'Windsor, ON, Canada';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-6 py-12 space-y-8">

        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Link to="/" className="text-sm text-blue-500 hover:underline">TheMoneyMap</Link>
            <span className="text-muted-foreground text-sm">›</span>
            <span className="text-sm text-muted-foreground">Terms of Service</span>
          </div>
          <h1 className="text-3xl font-bold">Terms of Service</h1>
          <p className="text-sm text-muted-foreground">Version {VERSION} — Effective {EFFECTIVE_DATE}</p>
        </div>

        <div className="prose prose-sm dark:prose-invert max-w-none space-y-6 text-sm leading-relaxed">

          <section>
            <h2 className="text-lg font-semibold mb-2">1. About TheMoneyMap</h2>
            <p>
              TheMoneyMap ("we", "us", "the service") is a personal finance tracking tool operated
              by an individual in Windsor, Ontario, Canada. By creating an account or using the service
              you agree to these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">2. Eligibility</h2>
            <p>
              You must be <strong>19 years of age or older</strong> to use TheMoneyMap. This applies
              across all of Canada (the age of majority in British Columbia, New Brunswick, Newfoundland
              and Labrador, Northwest Territories, Nova Scotia, Nunavut, Prince Edward Island,
              Saskatchewan, and Yukon is 19; in other provinces it is 18 — we use 19 as our single
              Canada-wide threshold). By creating an account you confirm that you meet this requirement.
            </p>
            <p>
              The service is not directed at individuals under 19 and we do not knowingly collect
              information from minors.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">3. Accounts</h2>
            <p>
              You are responsible for keeping your credentials secure and for all activity under your
              account. Notify us at {CONTACT_EMAIL} if you believe your account has been compromised.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">4. Pro Subscription</h2>
            <h3 className="font-medium mt-3 mb-1">Free trial</h3>
            <p>
              New accounts receive a <strong>90-day free trial</strong> of Pro features. No payment
              method is required during the trial. The trial ends 90 days from account creation;
              after that you must subscribe to keep Pro access. Your data is never deleted when a
              trial expires.
            </p>
            <h3 className="font-medium mt-3 mb-1">Pricing and billing</h3>
            <p>
              Pro is billed monthly. Prices are shown on the Upgrade page in your local currency
              (CAD, USD, INR, AED, AUD, EUR, GBP, SGD, JPY). The amount shown before you click "Subscribe" is the amount
              you will be charged each month, plus any applicable taxes. Billing is handled by
              Stripe, Inc.; your card details are never stored by TheMoneyMap.
            </p>
            <h3 className="font-medium mt-3 mb-1">Renewal</h3>
            <p>
              Your subscription renews automatically each month on the same calendar date as your
              original purchase. We do not send a renewal reminder email, so please set a reminder
              yourself if you wish to cancel before the next charge.
            </p>
            <h3 className="font-medium mt-3 mb-1">Cancellation</h3>
            <p>
              You can cancel at any time from <strong>Settings → Manage Subscription</strong> or
              from the Upgrade page. After you cancel, your Pro access continues until the end of
              the current paid billing period. No partial refunds are issued for unused days except
              as required by applicable law.
            </p>
            <h3 className="font-medium mt-3 mb-1">Refunds</h3>
            <p>
              Refund requests for charges made in error should be sent to {CONTACT_EMAIL} within
              30 days of the charge.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">5. Your Financial Data</h2>
            <p>
              You own all financial data you enter. We process it solely to provide the service to
              you. We do not sell, rent, or share your financial data with advertisers or data
              brokers. See our <Link to="/privacy" className="text-blue-500 hover:underline">Privacy Policy</Link> for details.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">6. AI Features</h2>
            <p>
              Pro accounts can use AI-powered transaction categorisation and insights. This feature
              sends anonymised transaction text to OpenAI's API for processing. You can disable
              AI features in Settings. Do not enter sensitive personal identifiers (e.g. SIN,
              passport number) in transaction notes.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">7. Acceptable Use</h2>
            <p>You may not use TheMoneyMap to:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Circumvent, probe, or overload the service's infrastructure.</li>
              <li>Use automated scripts to extract data at scale without our consent.</li>
              <li>Create accounts for others without their knowledge.</li>
              <li>Upload or transmit malicious code.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">8. Availability and Changes</h2>
            <p>
              We provide the service on a reasonable-effort basis. We may change features, pricing,
              or these Terms at any time. Material changes will be communicated by email or by an
              in-app notice at least 30 days before they take effect. Your continued use after the
              effective date constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">9. Limitation of Liability</h2>
            <p>
              TheMoneyMap is a tracking tool, not financial advice. We are not responsible for
              financial decisions made based on data displayed in the app. To the maximum extent
              permitted by Ontario law, our liability for any claim arising from your use of the
              service is limited to the subscription fees you paid in the 12 months preceding the
              claim.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">10. Governing Law</h2>
            <p>
              These Terms are governed by the laws of Ontario, Canada. Any disputes will be
              resolved in the courts of Ontario unless both parties agree otherwise.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-2">11. Contact</h2>
            <p>
              Questions about these Terms: <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-500 hover:underline">{CONTACT_EMAIL}</a><br />
              {CONTACT_ADDRESS}
            </p>
          </section>
        </div>

        <div className="pt-4 border-t flex gap-4 text-sm">
          <Link to="/privacy" className="text-blue-500 hover:underline">Privacy Policy</Link>
          <Link to="/login" className="text-muted-foreground hover:underline">Back to login</Link>
        </div>
      </div>
    </div>
  );
}
